import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  CheckCircle as SuccessIcon,
  Cancel as FailedIcon,
  Pending as PendingIcon,
  DoNotDisturbOn as CancelledIcon,
  Receipt as ReceiptIcon,
  AttachMoney as MoneyIcon,
  Search as SearchIcon,
  Block as BlockIcon,
  CheckCircleOutline as UnblockIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import { base_url as api_url } from "../../../utils/api-routes";

const MilanSathiTransactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [blockUser, setBlockUser] = useState(null);
  const [blockLoading, setBlockLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    pending: 0,
    failed: 0,
    cancelled: 0,
  });

  useEffect(() => {
    fetchTransactions();
  }, [page, rowsPerPage, statusFilter, searchQuery]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      let url = `${api_url}api/admins/transactions?page=${page + 1}&limit=${rowsPerPage}`;
      if (statusFilter) url += `&status=${statusFilter}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;

      const response = await fetch(url);
      const data = await response.json();
      if (data.success) {
        setTransactions(data.data);
        setTotalTransactions(data.pagination?.total || 0);
        setTotalRevenue(data.totalRevenue || 0);
        if (data.stats) {
          setStats(data.stats);
        }
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setSearchQuery(searchInput);
    setPage(0);
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearchQuery("");
    setPage(0);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleViewTransaction = (transaction) => {
    setSelectedTransaction(transaction);
    setDialogOpen(true);
  };

  const handleBlockClick = (user) => {
    setBlockUser(user);
    setBlockDialogOpen(true);
  };

  const handleBlockConfirm = async () => {
    if (!blockUser) return;
    setBlockLoading(true);
    try {
      const response = await fetch(`${api_url}api/admins/users/${blockUser._id}/block`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
      });
      const data = await response.json();
      if (data.success) {
        setSnackbar({
          open: true,
          message: data.message,
          severity: "success",
        });
        fetchTransactions();
      } else {
        setSnackbar({
          open: true,
          message: data.message || "Failed to update user status",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error blocking user:", error);
      setSnackbar({
        open: true,
        message: "Network error. Please try again.",
        severity: "error",
      });
    } finally {
      setBlockLoading(false);
      setBlockDialogOpen(false);
      setBlockUser(null);
    }
  };

  const handleStatClick = (status) => {
    setStatusFilter(status);
    setPage(0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getUserName = (user) => {
    if (!user) return "N/A";
    if (user.firstName) return `${user.firstName} ${user.lastName || ""}`.trim();
    return user.name || "N/A";
  };

  const getUserPhone = (user) => {
    if (!user) return "N/A";
    return user.mobile || user.phone || "N/A";
  };

  const getUserEmail = (user) => {
    if (!user) return "N/A";
    return user.email || "N/A";
  };

  const isUserBlocked = (user) => {
    return user?.status === "suspended";
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <SuccessIcon sx={{ color: "#4caf50" }} />;
      case "failed":
        return <FailedIcon sx={{ color: "#f44336" }} />;
      case "cancelled":
        return <CancelledIcon sx={{ color: "#9e9e9e" }} />;
      default:
        return <PendingIcon sx={{ color: "#ff9800" }} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "success";
      case "failed":
        return "error";
      case "cancelled":
        return "default";
      default:
        return "warning";
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: "bold" }}>
        Milan Sathi Transactions
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card 
            sx={{ 
              cursor: 'pointer', 
              transition: 'all 0.3s',
              border: statusFilter === '' ? '2px solid #1976d2' : 'none',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 } 
            }}
            onClick={() => handleStatClick('')}
          >
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="body2">Total</Typography>
                  <Typography variant="h4" fontWeight="bold" color="primary">
                    {stats.total || totalTransactions}
                  </Typography>
                </Box>
                <ReceiptIcon sx={{ fontSize: 40, color: "#1976d2" }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card 
            sx={{ 
              cursor: 'pointer', 
              transition: 'all 0.3s',
              border: statusFilter === 'completed' ? '2px solid #4caf50' : 'none',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 } 
            }}
            onClick={() => handleStatClick('completed')}
          >
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="body2">Success</Typography>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {stats.completed}
                  </Typography>
                </Box>
                <SuccessIcon sx={{ fontSize: 40, color: "#4caf50" }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card 
            sx={{ 
              cursor: 'pointer', 
              transition: 'all 0.3s',
              border: statusFilter === 'pending' ? '2px solid #ff9800' : 'none',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 } 
            }}
            onClick={() => handleStatClick('pending')}
          >
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="body2">Pending</Typography>
                  <Typography variant="h4" fontWeight="bold" color="warning.main">
                    {stats.pending}
                  </Typography>
                </Box>
                <PendingIcon sx={{ fontSize: 40, color: "#ff9800" }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card 
            sx={{ 
              cursor: 'pointer', 
              transition: 'all 0.3s',
              border: statusFilter === 'failed' ? '2px solid #f44336' : 'none',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 } 
            }}
            onClick={() => handleStatClick('failed')}
          >
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="body2">Failed</Typography>
                  <Typography variant="h4" fontWeight="bold" color="error.main">
                    {stats.failed}
                  </Typography>
                </Box>
                <FailedIcon sx={{ fontSize: 40, color: "#f44336" }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card 
            sx={{ 
              cursor: 'pointer', 
              transition: 'all 0.3s',
              border: statusFilter === 'cancelled' ? '2px solid #9e9e9e' : 'none',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 } 
            }}
            onClick={() => handleStatClick('cancelled')}
          >
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="body2">Cancelled</Typography>
                  <Typography variant="h4" fontWeight="bold" color="text.secondary">
                    {stats.cancelled}
                  </Typography>
                </Box>
                <CancelledIcon sx={{ fontSize: 40, color: "#9e9e9e" }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Revenue Card */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Total Revenue (Success Only)
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    ₹{totalRevenue.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </Typography>
                </Box>
                <MoneyIcon sx={{ fontSize: 40, color: "#4caf50" }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search & Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Search by Name, Phone or Email"
              variant="outlined"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {searchInput && (
                      <IconButton size="small" onClick={handleClearSearch}>
                        <ClearIcon />
                      </IconButton>
                    )}
                    <IconButton onClick={handleSearch} color="primary">
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => {
                setStatusFilter("");
                setSearchInput("");
                setSearchQuery("");
                setPage(0);
              }}
              sx={{ height: 56 }}
            >
              Reset
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Transactions Table */}
      <TableContainer component={Paper}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell sx={{ fontWeight: "bold" }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Phone</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Plan</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Amount</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Payment</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Reason</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.map((transaction, index) => (
                  <TableRow 
                    key={transaction._id} 
                    hover
                    sx={{
                      backgroundColor: isUserBlocked(transaction.user) ? "#fff3f3" : "inherit",
                    }}
                  >
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" fontWeight="bold">
                          {getUserName(transaction.user)}
                        </Typography>
                        {isUserBlocked(transaction.user) && (
                          <Chip label="Blocked" size="small" color="error" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {getUserPhone(transaction.user)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {getUserEmail(transaction.user)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {transaction.plan?.name || "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight="bold" color="primary">
                        ₹{transaction.amount?.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ textTransform: "capitalize" }}>
                        {transaction.paymentMethod || "Razorpay"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(transaction.status)}
                        label={transaction.status}
                        color={getStatusColor(transaction.status)}
                        size="small"
                        sx={{ textTransform: "capitalize" }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="error" sx={{ maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {transaction.failureReason || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {formatDate(transaction.createdAt)}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleViewTransaction(transaction)}
                        >
                          View
                        </Button>
                        {transaction.user && (
                          <Tooltip title={isUserBlocked(transaction.user) ? "Unblock User" : "Block User"}>
                            <IconButton
                              size="small"
                              color={isUserBlocked(transaction.user) ? "success" : "error"}
                              onClick={() => handleBlockClick(transaction.user)}
                            >
                              {isUserBlocked(transaction.user) ? <UnblockIcon fontSize="small" /> : <BlockIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {transactions.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      <Typography color="textSecondary" sx={{ py: 3 }}>
                        {searchQuery ? `No transactions found for "${searchQuery}"` : "No transactions found"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={totalTransactions}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 20, 50, 100]}
            />
          </>
        )}
      </TableContainer>

      {/* Transaction Details Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Transaction Details</DialogTitle>
        <DialogContent>
          {selectedTransaction && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="textSecondary">
                      Transaction ID
                    </Typography>
                    <Typography variant="body1" fontFamily="monospace">
                      {selectedTransaction.transactionId || selectedTransaction._id}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">Name</Typography>
                <Typography variant="body1" fontWeight="bold">
                  {getUserName(selectedTransaction.user)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">Phone</Typography>
                <Typography variant="body1">
                  {getUserPhone(selectedTransaction.user)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">Email</Typography>
                <Typography variant="body1">
                  {getUserEmail(selectedTransaction.user)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">User Status</Typography>
                <Chip
                  label={selectedTransaction.user?.status || "active"}
                  color={isUserBlocked(selectedTransaction.user) ? "error" : "success"}
                  size="small"
                  sx={{ textTransform: "capitalize" }}
                />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">Plan</Typography>
                <Typography variant="body1">
                  {selectedTransaction.plan?.name || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">Amount</Typography>
                <Typography variant="h6" color="primary" fontWeight="bold">
                  ₹{selectedTransaction.amount?.toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">Status</Typography>
                <Chip
                  label={selectedTransaction.status}
                  color={getStatusColor(selectedTransaction.status)}
                  size="small"
                  sx={{ textTransform: "capitalize" }}
                />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">Payment Method</Typography>
                <Typography variant="body1" sx={{ textTransform: "capitalize" }}>
                  {selectedTransaction.paymentMethod || "Razorpay"}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">Razorpay Payment ID</Typography>
                <Typography variant="body1" fontFamily="monospace" sx={{ wordBreak: 'break-all', fontSize: '0.85rem' }}>
                  {selectedTransaction.razorpayPaymentId || selectedTransaction.paymentId || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">Razorpay Order ID</Typography>
                <Typography variant="body1" fontFamily="monospace" sx={{ wordBreak: 'break-all', fontSize: '0.85rem' }}>
                  {selectedTransaction.razorpayOrderId || selectedTransaction.orderId || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">Date</Typography>
                <Typography variant="body1">
                  {formatDate(selectedTransaction.createdAt)}
                </Typography>
              </Grid>
              {selectedTransaction.status === 'cancelled' && selectedTransaction.cancelledAt && (
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Cancelled At</Typography>
                  <Typography variant="body1">
                    {formatDate(selectedTransaction.cancelledAt)}
                  </Typography>
                </Grid>
              )}
              {selectedTransaction.failureReason && (
                <Grid item xs={12}>
                  <Card variant="outlined" sx={{ borderColor: "#f44336" }}>
                    <CardContent>
                      <Typography variant="body2" color="textSecondary">Failure/Cancellation Reason</Typography>
                      <Typography variant="body1" color="error" fontWeight="bold">
                        {selectedTransaction.failureReason}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          {selectedTransaction?.user && (
            <Button
              color={isUserBlocked(selectedTransaction.user) ? "success" : "error"}
              variant="outlined"
              startIcon={isUserBlocked(selectedTransaction.user) ? <UnblockIcon /> : <BlockIcon />}
              onClick={() => {
                setDialogOpen(false);
                handleBlockClick(selectedTransaction.user);
              }}
            >
              {isUserBlocked(selectedTransaction.user) ? "Unblock User" : "Block User"}
            </Button>
          )}
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Block User Confirmation Dialog */}
      <Dialog open={blockDialogOpen} onClose={() => setBlockDialogOpen(false)}>
        <DialogTitle>
          {blockUser && isUserBlocked(blockUser) ? "Unblock User" : "Block User"}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {blockUser && isUserBlocked(blockUser)
              ? `Are you sure you want to UNBLOCK "${getUserName(blockUser)}" (${getUserPhone(blockUser)})? They will be able to use the app again.`
              : `Are you sure you want to BLOCK "${blockUser ? getUserName(blockUser) : ""}" (${blockUser ? getUserPhone(blockUser) : ""})? They will NOT be able to use the app.`
            }
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBlockDialogOpen(false)} disabled={blockLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={blockUser && isUserBlocked(blockUser) ? "success" : "error"}
            onClick={handleBlockConfirm}
            disabled={blockLoading}
          >
            {blockLoading ? <CircularProgress size={20} /> : (blockUser && isUserBlocked(blockUser) ? "Yes, Unblock" : "Yes, Block")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MilanSathiTransactions;
